import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment";

import { vocabularize } from "src/Common/vocab";
import {
  byFirstPunch,
  WEEK_CYCLE_MON_SUN,
  WEEK_CYCLE_SUN_SAT,
  startOfCycleWeek,
  crossoverOutcomes,
  formatClockRange,
  formatHhMm,
  hasOvertimeRule,
  isManualEntry,
  isBothCycles,
  onCallDaysInRange,
  displayDay,
  lunchAnchorsByDate,
  overtimeRuleChips,
  parseHhMm,
} from "./hours";
import {
  csvFilename,
  downloadCsv,
  isMissingRequiredLunch,
  missingLunchDates,
  technicianCsv,
} from "./breakdown";
import { openPrintTimecards } from "./printTimecards";
import TechnicianService from "src/Services/API/technician.service";
import DayEntriesEditor from "src/Common/DayEntriesEditor";
import WoGlanceModal from "./WoGlanceModal";
import SignOffChip from "./SignOffChip";
import { toast } from "react-toastify";
import { can, handleError } from "src/Common/function";

// A "00:00" bucket is rendered muted so a reviewer's eye lands on the hours
// that actually exist. Blank cells would be ambiguous — no hours logged and no
// value returned look the same, and both are legitimate here.
const HourCell = ({ value, bold, clockRange }) => {
  const minutes = parseHhMm(value);
  const text = formatHhMm(minutes);
  const tone = minutes === 0 ? "text-[#CBD5E1]" : bold ? "text-[#1F2A44]" : "text-[#45556C]";
  return (
    <td className={`px-6 py-3 text-center text-[13px] font-InterMedium ${tone}`}>
      <div>{text}</div>
      {/* The clock times sit under the duration rather than in columns of their
          own: the drill-down shares its columns with the totals above it, and
          four more would either break that alignment or squeeze it to nothing.
          Only entries logged since the tech app started capturing start/stop
          pairs have them, so the line is omitted rather than showing a dash
          row of empties for older hours. */}
      {clockRange ? (
        <div className="mt-0.5 text-[11px] leading-4 font-InterRegular text-[#94A3B8] whitespace-nowrap">
          {clockRange}
        </div>
      ) : null}
    </td>
  );
};

/**
 * One technician's summary row, with an expandable drill-down to the individual
 * timesheet entries behind the totals.
 *
 * The entries are already in memory — the page loaded every row to compute the
 * totals — so expanding costs nothing and needs no further requests.
 */
/**
 * Attention marker for a technician payroll has not assigned an overtime rule
 * to. Their hours still display — the numbers are real — but nothing can derive
 * overtime for them, so the row is flagged rather than quietly totalled.
 *
 * Click to explain rather than hover: the reason takes a sentence, and a
 * tooltip that vanishes on the way to reading it is no use on a payroll screen.
 */
const POPOVER_WIDTH = 300;

const UnassignedRuleFlag = ({ technicianName }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const popRef = useRef(null);
  const [rect, setRect] = useState(null);

  // Portaled to the body and positioned fixed. The table it lives in sits
  // inside overflow-hidden + overflow-x-auto, which would otherwise clip this
  // popover on the bottom rows — the same reason MultiSelectChipInput portals
  // its dropdown.
  const place = useCallback(() => {
    const anchor = wrapRef.current?.getBoundingClientRect();
    if (!anchor) return;
    const GAP = 6;
    const ESTIMATED_HEIGHT = 120;
    const flipUp = window.innerHeight - anchor.bottom < ESTIMATED_HEIGHT && anchor.top > ESTIMATED_HEIGHT;
    // Keep the panel on screen when the column sits near the right edge.
    const left = Math.max(8, Math.min(anchor.left, window.innerWidth - POPOVER_WIDTH - 8));
    setRect({
      left: Math.round(left),
      top: flipUp ? null : Math.round(anchor.bottom + GAP),
      bottom: flipUp ? Math.round(window.innerHeight - anchor.top + GAP) : null,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (wrapRef.current?.contains(event.target)) return;
      if (popRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <span ref={wrapRef} className="relative inline-flex shrink-0">
      <button
        type="button"
        // The row itself toggles the drill-down; this button is inside it, so
        // its click must not bubble or opening the explainer would also expand
        // the technician.
        onClick={(event) => {
          event.stopPropagation();
          setOpen((previous) => !previous);
        }}
        aria-label={`${technicianName} has no overtime policy assigned`}
        aria-expanded={open}
        className="w-5 h-5 rounded-full flex items-center justify-center text-[#B45309] hover:bg-[#FEF3C7]"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M8 1.75L15 14H1L8 1.75z"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinejoin="round"
            fill="#FEF3C7"
          />
          <path d="M8 6.25v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          <circle cx="8" cy="11.6" r="0.8" fill="currentColor" />
        </svg>
      </button>

      {open && rect
        ? createPortal(
            <div
              ref={popRef}
              onClick={(event) => event.stopPropagation()}
              style={{
                left: rect.left,
                top: rect.top ?? undefined,
                bottom: rect.bottom ?? undefined,
                width: POPOVER_WIDTH,
              }}
              className="fixed z-[60] cursor-default rounded-[8px] border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2.5 shadow-[0px_8px_24px_rgba(16,24,40,0.12)]"
            >
              <p className="text-[13px] leading-[18px] font-InterSemiBold text-[#92400E]">No overtime policy assigned</p>
              <p className="mt-1 text-[12px] leading-[18px] font-InterRegular text-[#92400E]">
                Payroll has not set whether {technicianName} earns overtime over 8 hrs/day or over 40 hrs/wk, so
                overtime cannot be calculated for these hours. Set it under{" "}
                <span className="font-InterSemiBold">Overtime Policies</span> at the top of this page.
              </p>
            </div>,
            document.body
          )
        : null}
    </span>
  );
};

// The loaded range as a plain string for the CSV footer. Kept in the same
// format the printed sheet uses so the two documents agree on what they cover.
// Declared above its use: the same rule the ReportFiltersSection memo had to
// learn, and cheaper to follow than to diagnose.
const cycleName = (cycle) => (cycle === WEEK_CYCLE_MON_SUN ? "Mon \u2013 Sun" : "Sun \u2013 Sat");

const rangeText = (range) =>
  range && range[0] && range[1]
    ? `${moment(range[0]).format("MMM D, YYYY")} – ${moment(range[1]).format("MMM D, YYYY")}`
    : null;

/**
 * The two ways one technician's breakdown leaves this screen.
 *
 * They sit in the drill-down header rather than on the summary row: both act on
 * the entries below them, and a download button on a collapsed row would export
 * detail the person has not looked at yet.
 */
const BreakdownActions = ({ technician, range, historyByUser, onCall }) => {
  // The row itself toggles the drill-down, so neither click may bubble — it
  // would collapse the section the file was just taken from.
  const stop = (event) => event.stopPropagation();

  return (
    <div className="flex items-center gap-2" onClick={stop}>
      <button
        type="button"
        onClick={() =>
          downloadCsv(
            csvFilename(technician, range),
            technicianCsv(technician, { rangeLabel: rangeText(range), onCall, range })
          )
        }
        title={`Download ${technician.technician}'s entries as a CSV`}
        className="h-[30px] px-3 rounded-md border border-[#7FAFFF] bg-[#EEF4FF] text-[#0065FD] text-[12px] font-InterSemiBold hover:bg-[#DCE8FF] hover:border-[#0065FD] inline-flex items-center gap-1.5"
      >
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M8 2.5v8m0 0L5 7.5M8 10.5l3-3M3 13.5h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Export Breakdown
      </button>
      <button
        type="button"
        onClick={() =>
          openPrintTimecards([technician], range, historyByUser, {
            [technician.technicianId]: onCall,
          })
        }
        title={`Print ${technician.technician}'s timecard`}
        className="h-[30px] px-3 rounded-md border border-[#7FAFFF] bg-[#EEF4FF] text-[#0065FD] text-[12px] font-InterSemiBold hover:bg-[#DCE8FF] hover:border-[#0065FD] inline-flex items-center gap-1.5"
      >
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M4.5 6V2.5h7V6M4.5 11.5h7V14h-7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          <rect x="2" y="6" width="12" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.5" />
        </svg>
        Print
      </button>
    </div>
  );
};

const TechnicianRow = ({
  technician,
  expanded,
  onToggle,
  onToggleSplit,
  savingSplit,
  range,
  onOpenHistory,
  onHoursSaved,
  historyByUser,
  onCall,
  weekCycle,
  onShowBothCycles,
  signoff,
  onSignoffChanged,
}) => {
  // Which breakdown row is open for editing, by index; one at a time, since a
  // save replaces a whole day and two drafts of one day could undo each other.
  const [editingIndex, setEditingIndex] = useState(null);
  // The work order open in the quick-look modal, by number.
  const [glanceWo, setGlanceWo] = useState(null);
  const [savingHours, setSavingHours] = useState(false);
  const canEditHours = can("time-management:editHours");

  // The stretch as the timesheet stores it: the entered figures and punches,
  // nothing derived. What the other stretches of the day are sent back as.
  const STORED_FIELDS = ["regularTime", "travelTime", "overTime", "doubleTime", "regularStart", "regularStop", "travelStart", "travelStop"];
  const asStored = (entry) =>
    Object.fromEntries(
      STORED_FIELDS.filter((field) => entry[field] != null && entry[field] !== "").map((field) => [field, entry[field]])
    );

  const saveRowHours = (row, entries, reason) => {
    // The server replaces the whole day of that work order, and the editor
    // opened on one stretch of it. The others go back exactly as they were,
    // in their place; the edited one is replaced by whatever the editor
    // holds now — one stretch, several, or none.
    const dayEntries = technician.rows
      .filter((other) => other.assignmentId === row.assignmentId && other.dayDiff === row.dayDiff)
      .flatMap((other) => (other === row ? entries : [asStored(other)]));
    setSavingHours(true);
    TechnicianService.replaceDayEntries({
      technicianId: row.assignmentId,
      dayDiff: row.dayDiff,
      entries: dayEntries,
      reason,
      source: "PAYROLL",
    })
      .then(() => {
        toast("Hours updated");
        setEditingIndex(null);
        // The whole range reloads: an edit here can move overtime onto a
        // different row or day, so the derived figures are not local to the
        // row that was touched.
        if (onHoursSaved) onHoursSaved();
      })
      .catch((error) => handleError(error))
      .finally(() => setSavingHours(false));
  };

  const { rows } = technician;
  // Manual entry is not an unclassified person: the figures are as typed by
  // design, and the warning would send payroll to assign a rule that would
  // then do nothing.
  const manual = isManualEntry(technician);
  const unassigned = !manual && !hasOvertimeRule(technician);
  // This row is one of two appearances for the same person — the hours they
  // recorded against branches on this cycle. `sliceOf` carries what the whole
  // week came to, so a half can say what it is a half of.
  const slice = technician.sliceOf;

  // Which of these entries are in a week this technician worked both calendars.
  //
  // A crossover is a property of the week, not of one entry, so every entry in
  // that week is part of what has to be judged. With thirty-odd rows on screen
  // the flag on the summary row says only that something crossed over
  // somewhere; this says where.
  const crossoverWeeks = new Set(technician.crossoverWeekStarts ?? []);
  const inCrossoverWeek = (row) =>
    Boolean(
      row.date &&
        crossoverWeeks.size > 0 &&
        crossoverWeeks.has(
          startOfCycleWeek(row.date, technician.weekStartsOn ?? 0).format("YYYY-MM-DD"),
        ),
    );
  const crossoverRowCount = technician.rows.filter(inCrossoverWeek).length;
  const measuredApart = Boolean(technician.measuredApart);
  const outcomes = slice ? crossoverOutcomes(technician) : null;
  // Both rules can be held at once, so this is a list rather than one label.
  const ruleLabel = overtimeRuleChips(technician).join(" + ") || null;
  // The server says whether those rules are the person's own or the branch's
  // default; the row says so too, since that decides what changes when the
  // branch rule does.
  const ruleSource = technician.rows?.[0]?.payRuleSource ?? null;
  const ruleTitle =
    ruleSource === "branch"
      ? "The branch's default policies — this technician has none of their own."
      : ruleSource === "role"
        ? "The branch's policies for this technician's role — they have none of their own."
        : ruleSource === "mixed"
        ? "Partly this technician's own policies, partly the branch's defaults."
        : ruleSource === "user"
          ? "This technician's own policies."
          : undefined;
  // Rows whose day could not be placed on a clock, so their overtime could not
  // be pinned to one work order. Declared here, above the JSX that reads it.
  const unattributedRows = rows.filter((row) => row.derivedOverTime == null).length;
  // Which row each day's lunch is shown against — the span it interrupted.
  const lunchAnchors = lunchAnchorsByDate(rows);
  // Worked days at a branch that requires lunch recording, with nothing said.
  const missingLunch = missingLunchDates(technician).length;
  // Days on call within the range. Shown beside the hours, never added to them.
  const onCallDays = onCallDaysInRange(onCall, range);
  // Entries and on-call days in one date order, the way the printed sheet
  // lays them, so a week reads top to bottom in the order it happened. Days
  // are visited once each; a day's entries keep their order and their index
  // (the editor and the lunch anchors address a row by it), and the day's on
  // call follows them — or stands alone where nothing was worked that day.
  // Rows with no date sort last.
  const dayOf = (row) => (row.date ? moment(row.date).format("YYYY-MM-DD") : "");
  const onCallSet = new Set(onCallDays);
  const days = [...new Set([...rows.map(dayOf), ...onCallDays])].sort((a, b) =>
    a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)
  );
  const sequence = days.flatMap((day, dayIndex) => {
    const onCall = onCallSet.has(day);
    // In the order they happened, by first punch, whatever order the server
    // sent them in. The index each carries still names the row for the
    // editor and the lunch anchor.
    const entries = byFirstPunch(
      rows.flatMap((row, index) => (dayOf(row) === day ? [{ kind: "entry", row, index }] : [])),
      (item) => item.row
    );
    // On call is a pill beside the day's date rather than a row of its own.
    // A day with on call and no entries still needs a row to carry the date.
    const items = entries.length ? entries : onCall ? [{ kind: "onCall", date: day }] : [];
    return items.map((item, position) => ({ ...item, dayIndex, onCall, firstOfDay: position === 0 }));
  });

  const OnCallPill = () => (
    <span
      title="On call this day. A note on the day, not hours; counts toward neither regular hours nor overtime."
      className="inline-flex self-center px-1.5 py-[1px] rounded-[5px] bg-[#EDE9FE] text-[#5B21B6] text-[10px] leading-4 font-InterSemiBold uppercase tracking-[0.4px]"
    >
      On call
    </span>
  );

  // A rail down the left of every row of a day, alternating between two
  // tones day to day, with a heavier rule where a new day begins — the
  // bracket that tells one day's rows from the next's. Lunch and on-call
  // bands carry the same rail as the entries they belong with.
  const dayRail = (item, first = item.firstOfDay) =>
    `border-l-[4px] ${item.dayIndex % 2 === 0 ? "border-l-[#1F2A44]" : "border-l-[#94A3B8]"}${
      first ? " border-t-2 border-t-[#CBD5E1]" : ""
    }`;
  // Derived overtime that contradicts a figure someone actually typed. Shown
  // rather than resolved: which one is right is a payroll judgement, and
  // quietly replacing the entered figure would hide that a change happened.
  //
  // Nothing entered is not a contradiction. The tech app records no overtime at
  // all — it is derived now — so a zero here is the ordinary case rather than a
  // competing answer, and flagging it put a "was 00:00" on very nearly every
  // technician. That trains people to ignore the badge, which is the opposite
  // of what it is for: the rare row where a typed figure and the derived one
  // genuinely disagree needs to stand out.
  const disagrees =
    technician.derived != null &&
    technician.enteredOverMinutes > 0 &&
    technician.enteredOverMinutes !== technician.overMinutes;

  return (
    <Fragment>
      <tr
        className="border-b border-[#F1F5F9] cursor-pointer hover:bg-[#F8FAFC]"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <td className="px-6 py-4">
          <div className="flex items-center gap-2">
            <svg
              className={`w-[12px] h-[12px] shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`}
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
            >
              <path d="M4 2l4 4-4 4" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-[14px] font-InterSemiBold text-[#1F2A44]">{technician.technician}</span>
            {/* Which rule produced the OT figure on this row. Without it the
                number is unexplained: 4 hrs of overtime on a 12-hour day is
                right under the daily rule and wrong under the weekly one, and
                the row is the only place that ambiguity can be settled. */}
            {manual ? (
              <span
                title="Manual entry: this technician types regular, overtime and travel hours, and the figures are as entered. No overtime is derived. Switch them under Hours System at the top of this page."
                className="shrink-0 px-2 py-[2px] rounded-[6px] bg-[#F1F5F9] text-[#475569] text-[11px] leading-4 font-InterMedium whitespace-nowrap"
              >
                Manual entry
              </span>
            ) : ruleLabel ? (
              <span
                title={ruleTitle}
                className="shrink-0 px-2 py-[2px] rounded-[6px] bg-[#EEF0F9] text-[#5F6477] text-[11px] leading-4 font-InterMedium whitespace-nowrap"
              >
                {ruleLabel}
                {ruleSource === "branch" ? " · branch" : ruleSource === "role" ? " · role" : ""}
              </span>
            ) : null}
            {/* Who changed this person's hours, when, and why — for the range
                on screen. Stops the click from toggling the row it sits in. */}
            {onOpenHistory && technician.technicianId != null ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenHistory(technician);
                }}
                title={`Hours history for ${technician.technician}`}
                className="shrink-0 inline-flex items-center gap-1 px-2 py-[2px] rounded-[6px] border border-[#C7DBFF] bg-[#EEF4FF] text-[#0065FD] text-[11px] leading-4 font-InterMedium whitespace-nowrap hover:bg-[#DCE8FF] hover:border-[#9CC0FF]"
              >
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.4" />
                  <path d="M8 4.5V8l2.4 1.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
                History
              </button>
            ) : null}
            {/* Whether this person has signed the finished weeks on screen —
                the digital form of the card that went round for a signature.
                Clicking it is where the office answers a reopen request,
                reminds, or signs on their behalf. */}
            <SignOffChip
              technician={technician}
              signoff={signoff}
              range={range}
              history={historyByUser?.[technician.technicianId]}
              onChanged={onSignoffChanged}
            />
            {unassigned ? <UnassignedRuleFlag technicianName={technician.technician} /> : null}
            {/* The branch requires a lunch on every worked day and some have
                none. Not an error in the figures — nothing came off — but a
                day payroll has to chase before the timecard is complete. */}
            {missingLunch > 0 ? (
              <span
                title={`${missingLunch} worked ${missingLunch === 1 ? "day has" : "days have"} no lunch recorded and no "no lunch taken" note. ${technician.technician}'s branch requires one.`}
                className="shrink-0 px-2 py-[2px] rounded-[6px] bg-[#FEF3C7] text-[#92400E] text-[11px] leading-4 font-InterMedium whitespace-nowrap"
              >
                {missingLunch} {missingLunch === 1 ? "day" : "days"} missing lunch
              </span>
            ) : null}
            {/* Hours on branches from the other week calendar. Not an error and
                not a correction — the chip marks a row whose week is worth a
                second look, and the drill-down says what was worked where. */}
            {slice ? (
              <span
                title={`Only part of ${technician.technician}'s week is on this row — the rest is under the other week cycle.`}
                className="shrink-0 px-2 py-[2px] rounded-[6px] bg-[#FEF3C7] text-[#92400E] text-[11px] leading-4 font-InterMedium whitespace-nowrap"
              >
                {measuredApart ? "Counted as two weeks" : `Part of a ${formatHhMm(slice.totalMinutes)} week`}
              </span>
            ) : null}
          </div>
        </td>
        <td className="px-6 py-4 text-center text-[13px] font-InterRegular text-[#64748B]">
          {technician.detailTruncated ? "—" : rows.length}
        </td>
        <td className="px-6 py-4 text-center text-[14px] font-InterSemiBold text-[#1F2A44]">
          {formatHhMm(technician.regularMinutes)}
        </td>
        <td className="px-6 py-4 text-center text-[14px] font-InterSemiBold text-[#1F2A44]">
          <span className="inline-flex items-center justify-end gap-1.5">
            {disagrees ? (
              <span
                title={`Derived from this technician's policy. ${formatHhMm(
                  technician.enteredOverMinutes
                )} was entered on the timesheets.`}
                className="text-[11px] leading-4 font-InterMedium text-[#B45309] bg-[#FEF3C7] rounded px-1.5 py-[1px]"
              >
                was {formatHhMm(technician.enteredOverMinutes)}
              </span>
            ) : null}
            {formatHhMm(technician.overMinutes)}
          </span>
        </td>
        <td className="px-6 py-4 text-center text-[14px] font-InterSemiBold text-[#1F2A44]">
          {formatHhMm(technician.doubleMinutes ?? 0)}
        </td>
        <td className="px-6 py-4 text-center text-[14px] font-InterSemiBold text-[#64748B]">
          {formatHhMm(technician.travelMinutes)}
        </td>
        {/* Days on call in the range. Purple like the bands below; never part
            of the total, which is why it sits before it rather than in it. */}
        <td className="px-6 py-4 text-center text-[14px] font-InterSemiBold text-[#5B21B6] whitespace-nowrap">
          {/* The unit rides on every value: every other column is hours, and
              a bare 4 in this one reads as four hours. */}
          {technician.onCallDays ? (
            <>
              {technician.onCallDays}
              <span className="ml-1 text-[11px] font-InterMedium text-[#7C3AED]">
                {technician.onCallDays === 1 ? "day" : "days"}
              </span>
            </>
          ) : (
            <span className="text-[#CBD5E1]">0 days</span>
          )}
        </td>
        <td className="px-6 py-4 text-center text-[14px] font-InterBold text-[#1F2A44]">
          {formatHhMm(technician.totalMinutes)}
        </td>
      </tr>

      {/* The detail rows are part of the same table as the summary row above,
          rather than a nested one. A nested table computes its own column
          widths, so RT/OT/TT never lined up with the totals they belong to —
          which on a payroll screen invites reading a number against the wrong
          column. Date / WO / Customer share the first two columns; the last
          column stays empty because there is no per-row total to show. */}
      {expanded && technician.detailTruncated ? (
        <tr className="bg-[#F1F5F9] border-b border-[#E2E8F0]">
          <td colSpan={8} className="px-6 py-3">
            <p className="pl-5 text-[12px] leading-[18px] font-InterRegular text-[#64748B]">
              The totals above cover this whole range and are complete. The individual entries
              behind them were past the limit on how many this screen loads at once, so there is
              nothing to break down here — narrow the date range, or filter to this technician, and
              load again to see them.
            </p>
          </td>
        </tr>
      ) : null}

      {expanded && !technician.detailTruncated ? (
        <>
          {slice ? (
            <tr className="bg-[#FEF3C7] border-b border-[#FDE68A]">
              <td colSpan={8} className="px-6 py-3">
                <div className="pl-5 text-[12px] leading-[18px] text-[#92400E]">
                  {/* Written for someone meeting this for the first time: what
                      was found, why it needs a person, what each choice does to
                      the numbers, and the button. The overtime figures are named
                      on both sides because that is the whole decision — splitting
                      can lose overtime, and a toggle that hides which way the
                      number moves is asking payroll to guess. */}
                  <p className="font-InterSemiBold text-[13px]">
                    {technician.technician} worked branches on two different pay weeks.
                  </p>

                  <p className="mt-1 font-InterRegular">
                    {cycleName(WEEK_CYCLE_SUN_SAT)} branches and {cycleName(WEEK_CYCLE_MON_SUN)} branches start their
                    week on different days, so these hours could be counted as one week or as two. Only a person can
                    say which is right for this technician.
                  </p>

                  {/* Every bucket, not just overtime. Splitting can move hours
                      out of double time while leaving overtime untouched — a
                      21-hour day counted as one week is 9 hours of double time,
                      and counted as two it is 1. A comparison that showed only
                      overtime would have reported no difference at all on
                      exactly that case. */}
                  {outcomes ? (
                    <div className="mt-2 grid gap-1 font-InterRegular">
                      {[
                        { label: "Counted as one week", figures: outcomes.together, on: !measuredApart },
                        { label: "Counted as two weeks", figures: outcomes.apart, on: measuredApart },
                      ].map((option) => (
                        <div key={option.label} className="flex flex-wrap items-baseline gap-x-2">
                          <span className={option.on ? "font-InterSemiBold" : ""}>
                            {option.label}
                            {option.on ? " (current)" : ""}:
                          </span>
                          <span className={option.on ? "font-InterSemiBold" : ""}>
                            {formatHhMm(option.figures.regularMinutes)} regular &middot;{" "}
                            {formatHhMm(option.figures.overMinutes)} overtime &middot;{" "}
                            {formatHhMm(option.figures.doubleMinutes)} double
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {/* With one cycle selected the rows on the other calendar are
                      hidden, and the flag reads as a mistake: "all my hours are
                      on one branch." Say what is hidden and offer to show it. */}
                  {weekCycle !== undefined && !isBothCycles(weekCycle) && onShowBothCycles ? (
                    <div className="mt-2 rounded-md border border-[#FDE68A] bg-white px-3 py-2">
                      <p className="font-InterRegular">
                        Only {cycleName(weekCycle)} branches are shown, so the{" "}
                        <span className="font-InterSemiBold">
                          {formatHhMm(Math.max(0, slice.totalMinutes - technician.totalMinutes))}
                        </span>{" "}
                        this technician worked on {cycleName(weekCycle === WEEK_CYCLE_MON_SUN ? 0 : 1)} branches
                        that week {slice.totalMinutes - technician.totalMinutes === 60 ? "is" : "are"} not on screen.
                      </p>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onShowBothCycles();
                        }}
                        className="mt-2 h-[28px] px-3 rounded-md bg-[#92400E] text-white text-[12px] font-InterSemiBold hover:bg-[#B45309]"
                      >
                        Show both week cycles
                      </button>
                    </div>
                  ) : null}

                  {crossoverRowCount > 0 ? (
                    <p className="mt-2 font-InterRegular">
                      The {crossoverRowCount} shaded{" "}
                      {crossoverRowCount === 1 ? "entry" : "entries"} below{" "}
                      {crossoverRowCount === 1 ? "is" : "are"} in a week where both calendars were worked.
                    </p>
                  ) : null}

                  <p className="mt-2 font-InterRegular">
                    {measuredApart ? (
                      <>
                        Each set of hours is being measured against its own pay week — this is saved, and applies
                        whenever this week is loaded. This row shows the {cycleName(technician.cycle)} hours only.
                      </>
                    ) : (
                      <>
                        All {formatHhMm(slice.totalMinutes)} are being counted as one week, on the{" "}
                        {cycleName(slice.measuredOn)} pay week this technician's own branch uses. This row shows the{" "}
                        {formatHhMm(technician.totalMinutes)} worked on {cycleName(technician.cycle)} branches — a part
                        of that week, not a week of its own.
                      </>
                    )}
                  </p>

                  <button
                    type="button"
                    disabled={savingSplit}
                    onClick={(event) => {
                      // Inside the drill-down, which the row click collapses.
                      event.stopPropagation();
                      onToggleSplit?.();
                    }}
                    className="mt-2 h-[30px] px-3 rounded-md border border-[#D97706] bg-white text-[#92400E] text-[12px] font-InterSemiBold hover:bg-[#FEF3C7] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {savingSplit
                      ? "Saving\u2026"
                      : measuredApart
                      ? "Count as one week instead"
                      : "Split into two weeks"}
                  </button>
                </div>
              </td>
            </tr>
          ) : null}

          {/* The export actions get their own band above the column headings
              rather than sharing that row: the headings line up with the
              numeric columns on the right, and putting buttons in the same row
              pushed them out of alignment with the totals they label. */}
          <tr className="bg-[#F1F5F9]">
            <td colSpan={8} className="px-6 pt-3">
              <div className="pl-5">
                <BreakdownActions technician={technician} range={range} historyByUser={historyByUser} onCall={onCall} />
              </div>
            </td>
          </tr>

          <tr className="bg-[#F1F5F9] border-b border-[#E2E8F0] border-l-[4px] border-l-transparent">
            <td colSpan={2} className="pl-2 pr-6 pt-3 pb-1">
              <div className="flex items-center gap-2 text-[11px] leading-4 font-InterBold text-[#94A3B8] uppercase tracking-[0.5px]">
                <span className="w-[112px] shrink-0 text-center">Date</span>
                <span className="w-[84px] shrink-0 text-center">{vocabularize("WO #")}</span>
                <span className="w-[104px] shrink-0 text-center">Branch</span>
                <span className="truncate">Customer</span>
              </div>
            </td>
            {["RT", "OT", "DT", "TT"].map((label) => (
              <td
                key={label}
                className="px-6 pt-3 pb-1 text-center text-[11px] leading-4 font-InterBold text-[#94A3B8] uppercase tracking-[0.5px] whitespace-nowrap"
              >
                {label}
              </td>
            ))}
            <td className="px-6 pt-3 pb-1" />
            <td className="px-6 pt-3 pb-1 text-center text-[11px] leading-4 font-InterBold text-[#94A3B8] uppercase tracking-[0.5px] whitespace-nowrap">
              Total
            </td>
          </tr>

          {sequence.map((item) => {
            // An on-call day in its place among the entries. A fact about the
            // day with no figure in any column: the purple of the summary-row
            // chip and the empty hour cells say it is not a line of hours, so
            // the row does not say so in words.
            if (item.kind === "onCall") {
              return (
                <tr key={`on-call-${item.date}`} className={`bg-white border-b border-[#E2E8F0] ${dayRail(item)}`}>
                  <td colSpan={2} className="pl-2 pr-6 py-2">
                    <div className="flex items-center gap-2 text-[13px]">
                      <span className="w-[112px] shrink-0 flex flex-col items-center gap-0.5 font-InterSemiBold text-[#1F2A44] whitespace-nowrap">
                        {displayDay(item.date)}
                        <OnCallPill />
                      </span>
                      <span className="font-InterRegular text-[#94A3B8]">No hours</span>
                    </div>
                  </td>
                  <td colSpan={6} className="px-6 py-2" />
                </tr>
              );
            }
            const { row, index } = item;
            // Shown once per day, against the span the lunch fell inside rather
            // than at the end of the day: a lunch interrupts a piece of work,
            // and where it falls is what decides which job earned the overtime.
            const showLunchHere = lunchAnchors.get(row.date) === index;
            const lunchRange = formatClockRange(row.lunchStart, row.lunchStop);
            return (
            // Rows carry no id, and one technician can legitimately log twice to
            // the same WO on the same day, so the index is the only stable key
            // available. The list is never reordered after grouping, so this is
            // safe.
            <Fragment key={`${row.woNumber}-${row.date}-${index}`}>
            <tr
              className={`${
                inCrossoverWeek(row)
                  ? "bg-[#FFFBEB] border-b border-[#FDE68A]"
                  : "bg-white border-b border-[#E2E8F0]"
              } ${dayRail(item)}`}
            >
              <td colSpan={2} className="pl-2 pr-6 py-2">
                <div className="flex items-center gap-2 text-[13px]">
                  {/* The date once per day, on its first row; the blank
                      beneath it is what groups the rest. */}
                  <span className="w-[112px] shrink-0 flex flex-col items-center gap-0.5 font-InterSemiBold text-[#1F2A44] whitespace-nowrap">
                    {item.firstOfDay ? displayDay(row.date) : ""}
                    {item.firstOfDay && item.onCall ? <OnCallPill /> : null}
                  </span>
                  {/* truncate, not whitespace-nowrap: the column is a fixed
                      width that cannot shrink, so a long number like
                      ADMIN-TRUCKSTOCK/CYPRESS overflowed it and printed on top
                      of the customer name. The full value stays on the title. */}
                  {/* A quick look at the work order without leaving the
                      screen. The full value stays on the title for long
                      numbers, as before. */}
                  {row.woNumber ? (
                    <button
                      type="button"
                      title={row.woNumber}
                      onClick={() => setGlanceWo(row.woNumber)}
                      className="w-[84px] shrink-0 text-center font-InterMedium text-[#0065FD] truncate hover:underline"
                    >
                      {row.woNumber}
                    </button>
                  ) : (
                    <span className="w-[84px] shrink-0 text-center font-InterMedium text-[#1F2A44]">—</span>
                  )}
                  <span
                    title={row.branch || undefined}
                    className="w-[104px] shrink-0 text-center font-InterRegular text-[#45556C] truncate"
                  >
                    {row.branch || "—"}
                  </span>
                  <span
                    title={row.customer || undefined}
                    className="truncate font-InterRegular text-[#45556C]"
                  >
                    {row.customer || "—"}
                  </span>
                </div>
              </td>
              {/* The derived split where the day could be placed on a clock,
                  the entered values otherwise. Overtime belongs to the work
                  order being worked when the day crossed the threshold, so it
                  is shown against that work order. */}
              <HourCell
                value={row.derivedRegularTime ?? row.regularTime}
                bold
                clockRange={formatClockRange(row.regularStart, row.regularStop)}
              />
              <HourCell value={row.derivedOverTime ?? row.overTime} bold />
              {/* Derived where the engine placed the row; otherwise whatever
                  was typed (manual entry), which is nothing for a row recorded
                  before the field existed. */}
              <HourCell value={row.derivedDoubleTime ?? row.doubleTime} bold />
              {/* Derived where the row could be placed on a clock: the travel
                  paid at the regular rate, which RT no longer includes. */}
              <HourCell
                value={row.derivedTravelTime ?? row.travelTime}
                clockRange={formatClockRange(row.travelStart, row.travelStop)}
              />
              <td className="pl-3 pr-6 py-2 text-left">
                {/* Only where the row can be addressed: rows served before the
                    assignment id and day offset were sent have nothing to save
                    against. */}
                {canEditHours && row.assignmentId != null && row.dayDiff != null ? (
                  <button
                    type="button"
                    onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                    className="inline-flex items-center gap-1 px-2 py-[2px] rounded-[6px] border border-[#C7DBFF] bg-[#EEF4FF] text-[#0065FD] text-[11px] leading-4 font-InterMedium whitespace-nowrap hover:bg-[#DCE8FF] hover:border-[#9CC0FF]"
                  >
                    {/* Pencil while closed, an × while open: the icon says what
                        the click does, the same way History carries a clock. */}
                    {editingIndex === index ? (
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      </svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path
                          d="M11.3 2.7a1.5 1.5 0 0 1 2.1 2.1L5.5 12.7 2.5 13.5l.8-3L11.3 2.7z"
                          stroke="currentColor"
                          strokeWidth="1.4"
                          strokeLinejoin="round"
                        />
                        <path d="M10 4l2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                      </svg>
                    )}
                    {editingIndex === index ? "Close" : "Edit"}
                  </button>
                ) : null}
              </td>
              {/* The row's own total, under the summary row's TOTAL: the four
                  figures beside it added up, so a day can be read across
                  without adding in one's head. */}
              <td className="px-6 py-3 text-center text-[13px] font-InterSemiBold text-[#1F2A44] whitespace-nowrap">
                {formatHhMm(
                  parseHhMm(row.derivedRegularTime ?? row.regularTime) +
                    parseHhMm(row.derivedOverTime ?? row.overTime) +
                    parseHhMm(row.derivedDoubleTime ?? row.doubleTime) +
                    parseHhMm(row.derivedTravelTime ?? row.travelTime)
                )}
              </td>
            </tr>

            {editingIndex === index ? (
              <tr className={`bg-[#EDE9FE] border-b border-[#DDD6FE] ${dayRail(item, false)}`}>
                <td colSpan={8} className="p-0">
                  <DayEntriesEditor
                    className="pl-11"
                    resetKey={`${row.assignmentId}|${row.dayDiff}`}
                    // Just this stretch. The day may hold several on the same
                    // work order, and opening them all as "Entry 1, Entry 2"
                    // made it unclear which line was being changed. The save
                    // puts the others back untouched (see saveRowHours).
                    initialEntries={[row]}
                    // Manual entry edits the typed durations; derived edits
                    // the clock times the figures come from.
                    manual={manual}
                    saving={savingHours}
                    requireReason
                    reasonPrompt="Why is this day being changed? Kept with the change in the hours history."
                    onSave={(entries, reason) => saveRowHours(row, entries, reason)}
                    onCancel={() => setEditingIndex(null)}
                  />
                </td>
              </tr>
            ) : null}

            {showLunchHere && lunchRange ? (
              <tr className={`bg-white border-b border-[#E2E8F0] ${dayRail(item, false)}`}>
                <td colSpan={2} className="px-6 py-1.5">
                  <div className="ml-[104px] w-[84px] flex items-center justify-center text-[12px] leading-[18px]">
                    <span className="font-InterSemiBold text-[#92400E]">Lunch</span>
                  </div>
                </td>
                {/* The deduction with its clock span beneath, laid out the way
                    the entry rows show a figure over its span in the RT column. */}
                <td className="px-6 py-1.5 text-center whitespace-nowrap">
                  <div className="text-[12px] leading-[18px] font-InterMedium text-[#92400E]">
                    -{formatHhMm(row.lunchMinutes)}
                  </div>
                  <div className="text-[11px] leading-4 font-InterRegular text-[#B45309]">{lunchRange}</div>
                </td>
                {/* Nothing under Total: the entry rows' totals are already
                    net of lunch, so repeating the deduction here would take
                    it off twice and the column would no longer add up to
                    the row above. */}
                <td className="px-6 py-1.5" colSpan={5} />
              </tr>
            ) : null}

            {/* The technician said no lunch was taken. Same amber band as a
                recorded lunch, so payroll's eye lands on it the same way — but
                no figure in the deduction column, because nothing came off.
                The reason is the point: a day with no deduction that has been
                accounted for, as opposed to one nobody said anything about. */}
            {showLunchHere && !lunchRange && row.noLunch ? (
              <tr className={`bg-white border-b border-[#E2E8F0] ${dayRail(item, false)}`}>
                <td colSpan={2} className="px-6 py-1.5">
                  <div className="flex items-baseline gap-2 pl-5 ml-[84px] text-[12px] leading-[18px]">
                    <span className="font-InterSemiBold text-[#92400E] shrink-0">Lunch</span>
                    <span className="font-InterMedium text-[#92400E] shrink-0">None taken</span>
                    {row.noLunchReason ? (
                      <span className="font-InterRegular text-[#92400E]">— {row.noLunchReason}</span>
                    ) : null}
                  </div>
                </td>
                <td className="px-6 py-1.5" colSpan={6} />
              </tr>
            ) : null}

            {/* Nothing recorded and nothing asserted, where the branch requires
                one. Shown in the same band so it is found the same way, and
                worded as what is missing rather than as a deduction. */}
            {showLunchHere && !lunchRange && !row.noLunch && isMissingRequiredLunch(row) ? (
              <tr className={`bg-white border-b border-[#E2E8F0] ${dayRail(item, false)}`}>
                <td colSpan={2} className="px-6 py-1.5">
                  <div className="flex items-baseline gap-2 pl-5 ml-[84px] text-[12px] leading-[18px]">
                    <span className="font-InterSemiBold text-[#92400E] shrink-0">Lunch</span>
                    <span className="font-InterMedium text-[#92400E] shrink-0">Required — not recorded</span>
                  </div>
                </td>
                <td className="px-6 py-1.5" colSpan={6} />
              </tr>
            ) : null}
            </Fragment>
            );
          })}

          {/* Only shown when something could not be attributed. When every row
              has clock times the numbers speak for themselves, and a permanent
              caveat under every technician is noise. */}
          {technician.derived && unattributedRows > 0 ? (
            <tr className="bg-[#F1F5F9] border-b border-[#E2E8F0]">
              <td colSpan={8} className="px-6 pt-1 pb-3">
                <p className="pl-5 text-[12px] leading-[18px] font-InterRegular text-[#64748B]">
                  {unattributedRows} of these {vocabularize("work orders")}{" "}
                  {unattributedRows === 1 ? "has" : "have"} no clock times, so overtime could not be attributed to a
                  particular one and those hours are shown as entered. The{" "}
                  <span className="font-InterSemiBold">{formatHhMm(technician.overMinutes)}</span> on the summary row is
                  the day or week total either way.
                </p>
              </td>
            </tr>
          ) : null}
        </>
      ) : null}

      {glanceWo ? <WoGlanceModal woNumber={glanceWo} onClose={() => setGlanceWo(null)} /> : null}
    </Fragment>
  );
};

export default TechnicianRow;
